import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    CASEBUILDER_BASE,
    DATA_MANAGER_HOME,
    DATA_MANAGER_WORKSPACE_LABEL,
    getDataManagerStep,
} from '../../../../../constants/setupRoadmapSteps';
import { getNaccIdFromDetails } from '../../../../../helpers/naccAlias';
import { useCaseList } from '../../shared/hooks/useCaseList';
import { useCaseCompleteness } from '../../shared/hooks/useCaseCompleteness';
import { validateItemForApprove } from '../../fileUpload/utils/validation';
import CaseLibraryTable from './CaseLibraryTable';
import CaseCreateWizard from './CaseCreateWizard';
import styles from '../CaseBuilderEnhanced.module.css';

const casebuilderStep = getDataManagerStep('casebuilder');

function CaseCompletenessBadge({ caseDoc }) {
    const { completenessPercent, slideCount, incompleteCount } = useCaseCompleteness(caseDoc);
    if (!slideCount) {
        return <span className={`${styles.badge} ${styles.badgeDraft}`}>No slides</span>;
    }
    if (incompleteCount > 0) {
        return (
            <span className={`${styles.badge} ${styles.badgeIncomplete}`}>
                {completenessPercent}% · {incompleteCount} incomplete
            </span>
        );
    }
    return <span className={`${styles.badge} ${styles.badgeReady}`}>{completenessPercent}% complete</span>;
}

export default function CaseLibraryPage() {
    const navigate = useNavigate();
    const { cases, isLoading, error } = useCaseList();
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');
    const [showWizard, setShowWizard] = useState(false);

    const enrichedCases = useMemo(() => {
        return cases.map((c) => ({
            ...c,
            naccId: getNaccIdFromDetails(c.details),
            slideCount: Array.isArray(c.items) ? c.items.length : 0,
        }));
    }, [cases]);

    const filtered = useMemo(() => {
        let list = enrichedCases;
        const q = search.trim().toLowerCase();
        if (q) {
            list = list.filter((c) => {
                const hay = [
                    c.networkIdentifier,
                    c.localIdentifier,
                    c.naccId,
                ].filter(Boolean).join(' ').toLowerCase();
                return hay.includes(q);
            });
        }
        if (filter === 'incomplete') {
            list = list.filter((c) => {
                const items = Array.isArray(c.items) ? c.items : [];
                if (!items.length) return false;
                return items.some((item) => !validateItemForApprove(item).isValid);
            });
        } else if (filter === 'quarantined') {
            list = list.filter((c) => c.quarantined === true);
        } else if (filter === 'has_slides') {
            list = list.filter((c) => c.slideCount > 0);
        }
        return list;
    }, [enrichedCases, search, filter]);

    const handleRowClick = (caseDoc) => {
        const id = caseDoc.networkIdentifier;
        if (id) {
            navigate(`${CASEBUILDER_BASE}/${id}?step=overview`);
        }
    };

    const handleCreated = (caseDoc) => {
        setShowWizard(false);
        const id = caseDoc?.networkIdentifier;
        if (id) {
            navigate(`${CASEBUILDER_BASE}/${id}?step=overview`);
        }
    };

    if (isLoading) {
        return <div className={styles.emptyState}>Loading cases…</div>;
    }

    if (error) {
        return (
            <div className={styles.page}>
                <h2 className={styles.pageTitle}>Case library</h2>
                <p className={styles.statusError}>{error}</p>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.headerTop}>
                <div>
                    <p className={styles.pageSubtitle} style={{ marginBottom: '0.35rem' }}>
                        <Link to={DATA_MANAGER_HOME}>
                            Step {casebuilderStep.number} of {casebuilderStep.total} · {DATA_MANAGER_WORKSPACE_LABEL}
                        </Link>
                    </p>
                    <h2 className={styles.pageTitle}>Case library</h2>
                    <p className={styles.pageSubtitle}>
                        Search, filter, and open cases in the enhanced workspace.
                    </p>
                </div>
                <button type="button" className={styles.buttonPrimary} onClick={() => setShowWizard(true)}>
                    New case
                </button>
            </div>

            <div className={styles.toolbar}>
                <input
                    type="search"
                    className={styles.input}
                    placeholder="Search network ID, local ID, NACC…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    aria-label="Search cases"
                />
                <select
                    className={styles.select}
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    aria-label="Filter cases"
                >
                    <option value="all">All cases</option>
                    <option value="incomplete">Incomplete slides</option>
                    <option value="quarantined">Quarantined</option>
                    <option value="has_slides">Has slides</option>
                </select>
            </div>

            <CaseLibraryTable
                cases={filtered}
                onRowClick={handleRowClick}
                renderCompleteness={(c) => <CaseCompletenessBadge caseDoc={c} />}
            />

            {filtered.length === 0 && (
                <p className={styles.emptyState}>No cases match your search.</p>
            )}

            <CaseCreateWizard
                open={showWizard}
                onClose={() => setShowWizard(false)}
                onCreated={handleCreated}
            />
        </div>
    );
}
